import React, { FC, useMemo, useState } from 'react';
import { Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useGetResourceQuery, useGetTrendsQuery } from '../healthWalletSlice';
import type { TrendGroup } from '../healthWalletSlice';
import { formatDate, formatPanelLabel, formatInterpretation, isAbnormalInterp, panelInterpColor, recordStats } from '../healthWalletConstants';
import type { GroupedSummary } from '../healthWalletConstants';
import type { SectionProps } from './TabRenderer';
import { obsBaseSection, obsLatestLabel, trendBadge } from './classify';
import type { ObsSectionKey } from './classify';
import { Sparkline } from './TrendsSections';
import { V1_ACCENT, V1_ACCENT_TINT, V1_ACCENT_BORDER } from './theme';
import { V1SearchField } from './V1SearchField';

// ============================================================================
// Observations tab sections (design: Polygon Health Trends Redesign/
// Observations Desktop.dc.html, in the DESIGN palette). Data = tier-1 groups
// from get_patient_resource('observations') incl. category + latest values;
// sparklines come FREE from the Trends cache (RTK share — no new SQL).
// Sections match the design 1:1 with ONE mechanism change: "Disease
// monitoring" → "Checked most often" = top-6 lab groups BY RECORD COUNT
// (disease-GENERAL per the CEO — frequency IS the monitoring signal; safe
// title for healthy patients too — Leo). Pinned favorites OMITTED (needs
// backend persistence per the no-client-storage rule).
// ============================================================================

const PAGE_WIDTH_SX = { maxWidth: { xs: '100%', sm: '94%' } };
const CARD_LIMIT = 6;
const MONITORED_N = 6;

// SINGLE source of truth for the tab's buckets — hero tiles, filter pills, and expandable sections all read
// THIS record (present-only), so the three can't drift. `pill` = pill text + hero tile label; `title` =
// section header; `tileBg/tileFg` = hero tile palette; `dot` = section dot; `blurb` = section intro.
// (monitored + lab are DISJOINT — monitored = the top-N labs pulled OUT of lab — so they show as two tiles.)
const SECTION_META: Record<ObsSectionKey, { title: string; pill: string; dot: string; tileBg: string; tileFg: string; blurb: string }> = {
  monitored:  { title: 'Checked most often',   pill: 'Checked most often', dot: '#6C4CE0', tileBg: '#efeaff', tileFg: '#6C4CE0', blurb: 'The measurements your care team has recorded most frequently.' },
  lab:        { title: 'Lab results',          pill: 'Lab results',        dot: '#34a760', tileBg: '#e6f6ec', tileFg: '#1a7f3c', blurb: 'These lab results come from blood samples, like your blood counts, kidney markers, and minerals.' },
  vital:      { title: 'Vital signs',          pill: 'Vital signs',        dot: '#3b7dd8', tileBg: '#eef3fb', tileFg: '#2f5fb0', blurb: 'These are quick body measurements taken at your visits, like blood pressure, pulse, and oxygen level.' },
  assessment: { title: 'Health assessments',   pill: 'Assessments',        dot: '#e8961e', tileBg: '#fdf0dd', tileFg: '#9a5b00', blurb: 'These are questions and check-ins your care team wrote down, like smoking or alcohol history.' },
  care:       { title: 'Care & documentation', pill: 'Care & docs',        dot: '#b0b0b6', tileBg: '#f0f0f3', tileFg: '#48484a', blurb: 'These entries describe how and where your care happened, such as positions, devices, and record details.' },
};
const SECTION_ORDER: ObsSectionKey[] = ['monitored', 'lab', 'vital', 'assessment', 'care'];

const useObservationBuckets = () => {
  const { data, isLoading } = useGetResourceQuery('observations');
  const { data: trends } = useGetTrendsQuery();
  const groups: GroupedSummary[] = useMemo(
    () => (data ? [...data.named, ...(data.other ? [{ ...data.other, name: data.other.name || 'Other / Uncategorized' }] : [])] : []),
    [data]
  );
  // Trends series by group name — sparkline source (observations resource only).
  const seriesByName = useMemo(() => {
    const m = new Map<string, TrendGroup>();
    (trends ?? []).forEach((t) => { if (t.resource === 'observations') m.set(t.name, t); });
    return m;
  }, [trends]);
  const buckets = useMemo(() => {
    const b: Record<ObsSectionKey, GroupedSummary[]> = { monitored: [], lab: [], vital: [], assessment: [], care: [] };
    const sorted = [...groups].sort((a, c) => c.count - a.count);
    sorted.forEach((g) => { b[obsBaseSection(g)].push(g); });
    b.monitored = b.lab.slice(0, MONITORED_N); // top labs by count — pulled OUT of Lab results
    b.lab = b.lab.slice(MONITORED_N);
    return b;
  }, [groups]);
  return { groups, buckets, seriesByName, totalRecords: data?.totalRecords ?? 0, rawRecords: data?.rawRecords ?? 0, isLoading };
};

// Deterministic section summary (design's generated paragraph — template, NOT AI).
const sectionSummary = (key: ObsSectionKey, list: GroupedSummary[]): string => {
  const intro = SECTION_META[key].blurb;
  if (!list.length) return intro;
  const totalRecords = list.reduce((a, g) => a + g.count, 0);
  const picks = list.slice(0, 2).map((g) => {
    const latest = obsLatestLabel(g);
    const short = (g.name ?? '').replace(/\s*\[.*?\]\s*/g, ' ').replace(/\s{2,}/g, ' ').trim();
    return latest
      ? `your most recent ${short.toLowerCase()} was ${latest}${g.lastDate ? ` (${formatDate(g.lastDate)})` : ''}`
      : `${short.toLowerCase()} was last recorded ${g.lastDate ? formatDate(g.lastDate) : '—'}`;
  });
  const mid = picks.length === 2 ? ` For example, ${picks[0]}, and ${picks[1]}.` : picks.length === 1 ? ` For example, ${picks[0]}.` : '';
  return `${intro} Your record has ${list.length} ${list.length === 1 ? 'type' : 'types'} here, with about ${totalRecords.toLocaleString('en-US')} entries over time.${mid}`;
};

// --- 1. Hero -----------------------------------------------------------------
export const ObservationsHero: FC<SectionProps> = () => {
  const { groups, buckets, totalRecords, rawRecords, isLoading } = useObservationBuckets();
  const { x, y, grouped } = recordStats(totalRecords, groups.length, rawRecords);
  if (isLoading) {
    return <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2, display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Container>;
  }
  const tile = (value: number, label: string, bg: string, fg: string) => (
    <Box sx={{ width: { xs: 92, sm: 104 }, flexShrink: 0, bgcolor: bg, borderRadius: '15px', p: '16px 10px', textAlign: 'center' }}>
      <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: fg, lineHeight: 1 }}>{value.toLocaleString('en-US')}</Typography>
      <Typography sx={{ fontSize: 11, color: fg, mt: 0.75, fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
      <Paper elevation={0} sx={{ borderRadius: '20px', p: '24px 26px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: V1_ACCENT, textTransform: 'uppercase', mb: 1.25 }}>
            Your record at a glance
          </Typography>
          <Typography sx={{ fontSize: 21, fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.2px', maxWidth: 580 }}>
            {grouped ? (
              <>Your record contains <strong>{x.toLocaleString('en-US')}</strong> measurements across <strong>{y.toLocaleString('en-US')}</strong> types of observations.</>
            ) : (
              <>Your record contains <strong>{y.toLocaleString('en-US')}</strong> {y === 1 ? 'observation' : 'observations'}.</>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {SECTION_ORDER.filter((k) => buckets[k].length > 0).map((k) => (
            <React.Fragment key={k}>{tile(buckets[k].length, SECTION_META[k].pill, SECTION_META[k].tileBg, SECTION_META[k].tileFg)}</React.Fragment>
          ))}
        </Box>
      </Paper>
    </Container>
  );
};

// --- 2. Explainer -------------------------------------------------------------
export const ObservationsExplainer: FC<SectionProps> = () => (
  <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
    <Box sx={{ bgcolor: V1_ACCENT_TINT, borderRadius: '16px', p: '16px 20px', display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 'none', width: 26, height: 26, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <InfoOutlinedIcon sx={{ color: V1_ACCENT, fontSize: 18 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 650, mb: 0.4 }}>About your measurements</Typography>
        <Typography sx={{ fontSize: 13, color: '#4a4a52', lineHeight: 1.5, maxWidth: 820 }}>
          These are the measurements and test results in your record, sorted into simple groups — lab tests, vital signs,
          and the assessments your care team documented. This page shows your record as documented and does not interpret
          results or provide medical advice.
        </Typography>
      </Box>
    </Box>
  </Container>
);

// --- Card ---------------------------------------------------------------------
const ObservationCard: FC<{ g: GroupedSummary; series?: TrendGroup; onOpen: () => void }> = ({ g, series, onOpen }) => {
  const latest = obsLatestLabel(g);
  const interpLabel = formatInterpretation(g.interpretation); // analyte's OWN flag — PRIMARY
  const panelLabel = formatPanelLabel(g.panel); // latest reading's panel interp — secondary to the own flag
  // Range status (Below/In/Above range), same as the Trends card — from the shared trend series' latest value
  // vs its reference range. Only when the group is chartable (numeric + a range); null otherwise.
  const badge = series && series.data.length > 1 ? trendBadge(series) : null;
  return (
    <Box onClick={onOpen} sx={{
      bgcolor: '#fdfcff', border: '1px solid #ece9f4', borderRadius: '14px', p: 2.25, cursor: 'pointer',
      '&:hover': { borderColor: V1_ACCENT_BORDER, bgcolor: 'white' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 650, letterSpacing: '-0.2px', lineHeight: 1.3, minWidth: 0 }}>{g.name}</Typography>
        {badge && (
          <Box sx={{ flex: 'none', fontSize: '10.5px', fontWeight: 700, px: 1.25, py: 0.5, borderRadius: '20px', bgcolor: badge.bg, color: badge.fg, whiteSpace: 'nowrap' }}>
            {badge.label}
          </Box>
        )}
      </Box>
      {/* Value + chart share ONE baseline — chart bottom aligned to the measurement (value, e.g. "224 K/uL")
          line; the "Latest · date" caption sits BELOW the row so it doesn't lift the chart off that line
          (Leo 2026-07-26). Flags sit below too. Negative top margin pulls the value back up (the taller
          chart row otherwise drops it). */}
      <Box sx={{ mt: -0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5 }}>
          {latest ? (
            <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130, minWidth: 0 }}>{latest}</Typography>
          ) : (
            <Typography sx={{ fontSize: 12, color: '#8e8e93' }}>Latest value not recorded</Typography>
          )}
          {series && series.data.length > 1 ? (
            // Color = the range bucket (Trends palette), OVERRIDDEN to abnormal-red when the analyte's OWN latest
            // interpretation is abnormal (Low/High/…). So green means BOTH in-range AND the lab flagged it normal, and
            // the color can never contradict the "Low"/"High" chip. The PANEL interp is NOT a driver — it's panel-wide
            // (some other analyte), so it must not redden a normal analyte. (Leo 2026-07-25.)
            <Sparkline points={series.data} refLow={series.refLow} refHigh={series.refHigh}
              color={isAbnormalInterp(g.interpretation) ? '#c0362c' : trendBadge(series).spark} w={150} h={42} />
          ) : null}
        </Box>
        {latest ? (
          <Typography sx={{ fontSize: 11, color: '#8e8e93', mt: 0.75 }}>
            Latest{g.lastDate ? ` · ${formatDate(g.lastDate)}` : ''}
          </Typography>
        ) : null}
      </Box>
      {interpLabel && (
        <Typography sx={{ fontSize: 11, fontWeight: 700, mt: 0.75, color: isAbnormalInterp(g.interpretation) ? '#bd352b' : '#8e8e93' }}>
          Interpretation: {interpLabel}
        </Typography>
      )}
      {panelLabel && (
        <Typography sx={{ fontSize: 11, mt: 0.5, color: '#8e8e93' }}>
          {panelLabel.lead}<strong style={{ color: panelInterpColor(panelLabel.interp) }}>{panelLabel.interp}</strong>.
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, pt: 1.25, borderTop: '1px solid #f0eef6' }}>
        <Typography sx={{ fontSize: 12, color: '#6b6b70' }}>{g.count.toLocaleString('en-US')} records</Typography>
        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: V1_ACCENT, whiteSpace: 'nowrap' }}>View history ›</Typography>
      </Box>
    </Box>
  );
};

// --- 3. Body: search + section pills + sections w/ summaries ---------------------
type FilterKey = 'all' | ObsSectionKey;

export const ObservationsBody: FC<SectionProps> = ({ openDetail }) => {
  const { buckets, seriesByName, isLoading } = useObservationBuckets();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});

  const q = search.trim().toLowerCase();
  const matches = (g: GroupedSummary) => !q || (g.name || 'other').toLowerCase().includes(q);

  if (isLoading) return null; // hero shows the spinner

  const pill = (key: FilterKey, label: string) => {
    if (key !== 'all' && buckets[key as ObsSectionKey].length === 0) return null;
    return (
      <Box key={key} onClick={() => setFilter(key)} sx={{
        fontSize: 13, fontWeight: 600, px: 2, py: 1, borderRadius: '20px', cursor: 'pointer',
        bgcolor: filter === key ? V1_ACCENT : 'white', color: filter === key ? 'white' : '#3a3a3c',
        border: `1px solid ${filter === key ? V1_ACCENT : '#e0e0e6'}`,
      }}>{label}</Box>
    );
  };

  // Default-open = the FIRST NON-EMPTY section in display order (Leo 2026-07-14).
  const firstNonEmpty = SECTION_ORDER.find((k) => buckets[k].length > 0);
  const defaultOpen = (key: string) => key === firstNonEmpty;

  const section = (key: ObsSectionKey) => {
    const meta = SECTION_META[key];
    const list = buckets[key];
    const visible = (filter === 'all' || filter === key) && list.filter(matches).length > 0;
    if (!visible) return null;
    const full = list.filter(matches);
    const forced = !!q || filter === key;
    const isOpen = forced ? true : (open[key] ?? defaultOpen(key));
    const all = !!q || !!showAll[key];
    const cards = isOpen ? (all ? full : full.slice(0, CARD_LIMIT)) : [];
    return (
      <Paper key={key} elevation={0} sx={{ borderRadius: '18px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', overflow: 'hidden' }}>
        <Box onClick={() => setOpen((s) => ({ ...s, [key]: !(s[key] ?? defaultOpen(key)) }))} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '18px 22px', cursor: 'pointer' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: meta.dot, flex: 'none' }} />
          <Typography component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', flex: 1 }}>{meta.title}</Typography>
          <Box sx={{ fontSize: '12.5px', fontWeight: 700, color: '#6b6b70', bgcolor: '#f0f0f3', px: 1.4, py: 0.5, borderRadius: '20px' }}>{full.length}</Box>
          <Typography sx={{ fontSize: 13, color: '#8e8e93', width: 16, textAlign: 'center' }}>{isOpen ? '▾' : '▸'}</Typography>
        </Box>
        {isOpen && (
          <Box sx={{ px: 2.75, pb: 2.75 }}>
            {/* The design's generated section summary — deterministic template, not AI. */}
            <Typography sx={{ fontSize: 13, color: '#6b6b70', lineHeight: 1.55, mb: 1.75, maxWidth: 860 }}>
              {sectionSummary(key, full)}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 1.75 }}>
              {cards.map((g, i) => (
                <ObservationCard key={`${g.name}-${i}`} g={g} series={seriesByName.get(g.name)} onOpen={() => openDetail(g)} />
              ))}
            </Box>
            {full.length > CARD_LIMIT && (
              <Typography onClick={() => setShowAll((s) => ({ ...s, [key]: !s[key] }))}
                sx={{ textAlign: 'center', mt: 2, fontSize: 13, fontWeight: 600, color: V1_ACCENT, cursor: 'pointer' }}>
                {all ? 'Show fewer' : `Show all ${full.length} →`}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  const noResults = !!q && !SECTION_ORDER.some((k) => buckets[k].some(matches));

  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2.5, pb: 12 }}>
      <V1SearchField value={search} onChange={setSearch} placeholder="Search measurements and results" />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {pill('all', 'All')}
        {SECTION_ORDER.map((k) => pill(k, SECTION_META[k].pill))}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {SECTION_ORDER.map((k) => section(k))}
      </Box>
      {noResults && (
        <Typography sx={{ p: '40px 26px', textAlign: 'center', color: '#8e8e93', fontSize: 15 }}>
          No measurements match &ldquo;{search}&rdquo;.
        </Typography>
      )}
      <Box sx={{ mt: 4.5, pt: 2.75, borderTop: '1px solid #e2e2e8', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 12, color: '#aeaeb2', lineHeight: 1.55, maxWidth: 660, mx: 'auto' }}>
          This page organizes the measurements and results reported by your healthcare providers. It presents your health
          record as documented and does not interpret results or provide medical advice.
        </Typography>
      </Box>
    </Container>
  );
};
